import React, { SetStateAction, useContext, useState } from "react";
import styles from "./mortgage-view.module.scss";
import { Property } from "../../types/Property";
import { formatAmountNaira } from "../../utils/generalUtils";
import InputWithHeader from "../../components/Input/InputWithHeader";
import ModalLayout from "../../components/ModalLayout";
import PopUp from "../../components/Insurance/PopUp";
import {
  PrequalifyRequest,
  PrequalifyResponseData,
} from "../../types/mortgage";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { mortgagePrequalifyApi } from "../../swr/mortgage";
import { useQuery } from "../../hooks/useQuery";
import { useHistory } from "react-router";
import ToastContext from "../../contexts/ToastContext";
import LoadingContext from "../../contexts/LoadingContext";
import SuccessModal from "../../components/Modal/success-modal";
import { useLocation } from "react-router-dom";
import EligibilityModal from "../../components/Insurance/EligibilityModal";
import DateInputWithHeader from "../../components/Input/DateInputWithHeader";

type MortgageApplicationProps = {
  propertyId: number | undefined;
  price: number | undefined;
  setMortgageSuccess?: React.Dispatch<SetStateAction<boolean>>;
  property: Property | undefined;
};

export const mortgageSchema: yup.ObjectSchema<PrequalifyRequest> = yup.object({
  source: yup.number().required(),

  location: yup.number().required("Location is required"),

  applicantType: yup.number().required("Employment type is required"),

  loanAmount: yup.number().min(0).required(),

  downPayment: yup.number().min(0).required(),

  upfrontFees: yup.number().min(0).required(),

  interestRate: yup
    .number()
    .required("Mortgage funder is required")
    .moreThan(0, "Mortgage funder is required"),

  tenorInMonths: yup
    .number()
    .required("Loan duration is required")
    .min(1, "Loan duration is required")
    .when(["interestRate", "applicantType"], ([interestRate, applicantType], schema) => {
      if (interestRate === 27.5 && applicantType === 1) {
        return schema.max(10, "Maximum duration is 10 years for business owners");
      }
      return schema.max(20, "Maximum duration is 20 years");
    }),

  currency: yup.string().required(),

  repaymentFrequency: yup.number().required("Repayment frequency is required"),

  monthlyIncome: yup
    .number()
    .required("Monthly income is required")
    .min(1, "Monthly income must be greater than 0"),

  coBorrowerMonthlyIncome: yup
    .number()
    .defined()
    .when("hasCoBorrower", {
      is: true,
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.default(0),
    }),

  coBorrowerCurrency: yup.string().default(""),

  dateOfBirth: yup
    .string()
    .required("Date of birth is required"),

  useRSA: yup.boolean().required(),

  rsaNumber: yup.string().when(["useRSA", "location"], {
    is: (useRSA: boolean, location: number) =>
      useRSA === true && location === 0,
    then: (schema) => schema.required("RSA number is required"),
    otherwise: (schema) => schema.default("").notRequired(),
  }) as yup.StringSchema<string>,

  pensionAdmin: yup.string().when(["useRSA", "location"], {
    is: (useRSA: boolean, location: number) =>
      useRSA === true && location === 0,
    then: (schema) => schema.required("Pension administrator is required"),
    otherwise: (schema) => schema.default("").notRequired(),
  }) as yup.StringSchema<string>,

  propertyId: yup.number().required(),

  hasCoBorrower: yup.boolean().optional(),

  hasRSA: yup.boolean().optional(),
});

const MortgageApplication = ({
  propertyId,
  property,
}: MortgageApplicationProps) => {
  let query = useQuery();
  let history = useHistory();
  const path = useLocation();
  let redirect = query.get("morf");

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<PrequalifyRequest>({
    resolver: yupResolver(mortgageSchema),
    defaultValues: {
      source:
        redirect === "external-mort"
          ? 1
          : redirect === "homebuddy-mort"
          ? 0
          : 2,
      location: 0,
      applicantType: 0,
      loanAmount: property && property?.totalPrice ? property?.totalPrice : 0,
      downPayment: 0,
      upfrontFees: 0,
      interestRate: 0,
      tenorInMonths: 0,
      currency: "NGN",
      repaymentFrequency: 0,
      monthlyIncome: 0,
      coBorrowerMonthlyIncome: 0,
      coBorrowerCurrency: "",
      dateOfBirth: "",
      useRSA: false,
      rsaNumber: "",
      pensionAdmin: "",
      propertyId: propertyId ?? 0,
      hasCoBorrower: undefined,
      hasRSA: undefined,
    },
  });

  const { openError } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const [submitSuccessful, setSubmitSuccessful] = useState(false);

  // Watch fields needed for conditional rendering
  const location = watch("location");
  const currency = watch("currency");
  const hasCoBorrower = watch("hasCoBorrower");
  const hasRSA = watch("hasRSA");
  const monthlyIncome = watch("monthlyIncome");
  const coBorrowerMonthlyIncome = watch("coBorrowerMonthlyIncome");
  const coBorrowerCurrency = watch("coBorrowerCurrency");
  const useRSA = watch("useRSA");
  const interestRate = watch("interestRate");
  const applicantType = watch("applicantType");

  const isNigeria = location === 0;
  const isDiaspora = location === 1;

  const maxTenorYears = isDiaspora
    ? 10
    : interestRate === 27.5 && applicantType === 1
    ? 10
    : 20;

  React.useEffect(() => {
    const currentTenor = watch("tenorInMonths");
    if (currentTenor > maxTenorYears) {
      setValue("tenorInMonths", maxTenorYears, { shouldValidate: true });
    }
  }, [maxTenorYears]);

  const getCurrencySymbol = (code: string) => {
    const symbols: Record<string, string> = {
      NGN: "₦",
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      CAD: "CA$",
      CHF: "CHF",
    };
    return symbols[code] ?? code;
  };

  const showCombinedIncome =
    hasCoBorrower === true &&
    (isNigeria || (currency === "NGN" && coBorrowerCurrency === "NGN"));

  const [dropDownStates, setDropDownStates] = useState({
    location: false,
    interestRate: false,
    applicantType: false,
    repaymentFrequency: false,
    hasCoBorrower: false,
    hasRSA: false,
    useRSA: false,
    currency: false,
    coBorrowerCurrency: false,
    pensionAdmin: false,
  });

  const [eligibilityRows, setEligibilityRows] = useState([
    { label: "Monthly Repayment", value: "" },
    { label: "Down Payment", value: "" },
    { label: "Debt to Income Ratio", value: "" },
  ]);

  const [isEligible, setIsEligible] = useState<undefined | boolean>(undefined);
  const [eligibilityMessage, setEligibilityMessage] = useState<string>("");
  const [prequalifyResult, setPrequalifyResult] =
    useState<PrequalifyResponseData | null>(null);
  const [submittedReq, setSubmittedReq] = useState<PrequalifyRequest | null>(
    null
  );

  const isHomeBuddy = redirect === "homebuddy-mort";
  const downPaymentValue = watch("downPayment");

  React.useEffect(() => {
    if (isHomeBuddy && property?.totalPrice) {
      const dp = Number(downPaymentValue) || 0;
      setValue("loanAmount", Math.max(0, property.totalPrice - dp), { shouldValidate: false });
    }
  }, [downPaymentValue, isHomeBuddy, property?.totalPrice]);

  const locationOptions = [
    {
      name: "Nigeria",
      value: 0,
    },
    {
      name: "Diaspora",
      value: 1,
    },
  ];

  const interestRateOptions = [
    {
      name: "Access Mortgage - 27.5%",
      value: 27.5,
    },
    {
      name: "Access MREIF - 9.75%",
      value: 9.75,
    },
  ];

  const employmentTypeOptions = [
    {
      name: "Salary Earner",
      value: 0,
    },
    {
      name: "Business Owner",
      value: 1,
    },
  ];

  const repaymentFreqOptions = [
    {
      name: "Monthly",
      value: 0,
    },
    {
      name: "Quarterly",
      value: 1,
    },
    {
      name: "BiAnnual",
      value: 2,
    },
  ];

  const currencyOptions = ["USD", "NGN", "EUR", "GBP", "JPY", "CAD", "CHF"];

  const pfaOptions = [
    "Stanbic IBTC Pension Managers Limited",
    "Premium Pension Limited",
    "Leadway Pensure PFA Limited",
    "Sigma Pensions Limited",
    "Pensions Alliance Limited",
    "Access ARM Pension Managers Limited",
    "Trustfund Pensions Plc",
    "First Guarantee Pension Limited",
    "Legacy Pension Managers Limited",
    "NLPC Pension Fund Administrators Limited",
    "CrusaderSterling Pensions Limited",
    "AlICO Pension Managers Limited",
    "OAK Pensions Limited",
    "AXA Masard Pension Limited",
    "IEl-Anchor Pension Managers Limited",
    "APT Pension Fund Managers Limited",
    "Investment One Pension Managers Limited",
    "Future Unity Glanvils Pensions Limited",
    "Fidelity Pension Managers",
    "Radix Pension Managers Limited",
    "NPF Pensions Limited",
  ];

  const toggleDropdown = (key: keyof typeof dropDownStates, open: boolean) => {
    setDropDownStates((prev) => ({ ...prev, [key]: open }));
  };

  const clickOutSuccessModal = () => {
    setSubmitSuccessful(false);
    history.push("/mortgage-categories");
  };

  const stripCurrency = (val: number | string | undefined): number => {
    if (val === undefined || val === null) return 0;
    return Number(
      String(val)
        .replace(/^[^0-9]+/, "")
        .replace(/,/g, "")
    );
  };

  const onContinue = () => {
    if (redirect) {
      history.push(
        `/${redirect}?dippa=${prequalifyResult?.id}` +
          (submittedReq?.applicantType === 1 ? "&epytppa=business" : "") +
          `&loc=${submittedReq?.location}`
      );
    } else if (isEligible) {
      reset();
    }
    setIsEligible(undefined);
  };

  const onSubmit = async (req: PrequalifyRequest) => {
    setLoading(true);
    try {
      req.loanAmount = stripCurrency(req.loanAmount);
      req.downPayment = stripCurrency(req.downPayment);
      req.upfrontFees = stripCurrency(req.upfrontFees);
      req.monthlyIncome = stripCurrency(req.monthlyIncome);
      req.coBorrowerMonthlyIncome = stripCurrency(req.coBorrowerMonthlyIncome);

      const { data, error } = await mortgagePrequalifyApi(req);
      if (data?.data) {
        setPrequalifyResult(data.data);
        setSubmittedReq(req);
        setEligibilityRows([
          { label: "Monthly Repayment", value: formatAmountNaira(data.data.monthlyRepayment) },
          { label: "Down Payment", value: formatAmountNaira(data.data.downPayment) },
          { label: "Debt to Income Ratio", value: `${data.data.debtToIncomeRatio}%` },
        ]);
        setEligibilityMessage(data.message ?? "");
        setIsEligible(data.data.isEligible);
      }

      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={
            path.pathname.startsWith("/properties")
              ? styles["container-full"]
              : styles["container"]
          }
        >
          <span className={styles.instruction}>
            Please enter your details below to check your eligibility.
          </span>

          {property ? (
            <div className={styles.flexBet}>
              <div className={styles["top-header"]}>
                <span className={styles["top-header-text"]}>
                  Property Amount
                </span>
                <div className={styles["top-header-inp"]}>
                  <span className={styles["top-header-inp-text"]}>
                    {property?.totalPrice
                      ? formatAmountNaira(property.totalPrice)
                      : null}
                  </span>
                </div>
              </div>
              <div className={styles["top-header"]}>
                <span className={styles["top-header-text"]}>
                  Interest Ratio
                </span>
                <div className={styles["top-header-inp"]}>
                  <span className={styles["top-header-inp-text"]}>
                    {interestRateOptions.find((o) => o.value === watch("interestRate"))
                      ? `${watch("interestRate")}%`
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          <div className={styles.flexBet}>
            {/* ── Location ── */}
            <div className={styles.input}>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Where Are You Located?"
                    placeholder="Select Location"
                    type="dropdown"
                    value={
                      locationOptions.find((l) => l.value === field.value)
                        ?.name ?? ""
                    }
                    onPressDropDown={() => toggleDropdown("location", true)}
                    error={errors.location?.message}
                  />
                )}
              />
            </div>

            {/* ── Mortgage Funder ── */}
            <div className={styles.input}>
              <Controller
                name="interestRate"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Mortgage Funder"
                    placeholder="Select Mortgage Funder"
                    type="dropdown"
                    value={
                      interestRateOptions.find((l) => l.value === field.value)
                        ?.name ?? ""
                    }
                    onPressDropDown={() => toggleDropdown("interestRate", true)}
                    error={errors.interestRate?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flexBet}>
            {/* ── Employment Type ── */}
            <div className={styles.input}>
              <Controller
                name="applicantType"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Select Employment Type"
                    placeholder="Employment Type"
                    type="dropdown"
                    value={
                      employmentTypeOptions.find((l) => l.value === field.value)
                        ?.name ?? ""
                    }
                    onPressDropDown={() =>
                      toggleDropdown("applicantType", true)
                    }
                    error={errors.applicantType?.message}
                  />
                )}
              />
            </div>

            {/* ── Date of Birth ── */}
            <div className={styles.input}>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <DateInputWithHeader
                    header="Date of Birth"
                    placeholder="Select Date of Birth"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flexBet}>
            {/* ── Tenor ── */}
            <div className={styles.input}>
              <Controller
                name="tenorInMonths"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header={`Loan Duration in Years (${maxTenorYears} years max)`}
                    placeholder="Select Duration"
                    type="number"
                    value={field.value || 0}
                    formKey="tenorInMonths"
                    handleChange={(_, val) => field.onChange(Math.min(Number(val), maxTenorYears))}
                    error={errors.tenorInMonths?.message}
                  />
                )}
              />
            </div>

            {/* ── Repayment Frequency ── */}
            <div className={styles.input}>
              <Controller
                name="repaymentFrequency"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Repayment Frequency"
                    placeholder="Select Repayment Frequency"
                    type="dropdown"
                    value={
                      repaymentFreqOptions.find((l) => l.value === field.value)
                        ?.name ?? ""
                    }
                    onPressDropDown={() =>
                      toggleDropdown("repaymentFrequency", true)
                    }
                    error={errors.repaymentFrequency?.message}
                  />
                )}
              />
            </div>
          </div>

          {/* ── Income Currency (Diaspora only) ── */}
          {isDiaspora && (
            <div className={styles.flexBet}>
              <div className={styles.input}>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <InputWithHeader
                      header="Income Currency"
                      placeholder="Select Currency"
                      type="dropdown"
                      value={field.value || ""}
                      onPressDropDown={() => toggleDropdown("currency", true)}
                      error={errors.currency?.message}
                    />
                  )}
                />
              </div>
            </div>
          )}

          <div className={styles.flexBet}>
            <div className={styles.input}>
              <Controller
                name="loanAmount"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header={isHomeBuddy || (property && property?.totalPrice) ? "Loan Amount" : "Property Price"}
                    placeholder={isHomeBuddy || (property && property?.totalPrice) ? "" : "Enter Property Price"}
                    // Use "amount" only for NGN, otherwise plain "string" so we
                    // can prepend the correct symbol via the currencySymbol prop
                    type={isNigeria || currency === "NGN" ? "amount" : "string"}
                    currencySymbol={getCurrencySymbol("NGN")}
                    value={field.value || 0}
                    formKey="loanAmount"
                    handleChange={(_, val) =>
                      field.onChange(Number(val.replace(/\D/g, "")))
                    }
                    error={errors.loanAmount?.message}
                    editable={isHomeBuddy || (property && property?.totalPrice) ? false : true}
                  />
                )}
              />
            </div>
            <div className={styles.input}>
              <Controller
                name="downPayment"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Down Payment"
                    placeholder="Enter your proposed Down Payment"
                    // Use "amount" only for NGN, otherwise plain "string" so we
                    // can prepend the correct symbol via the currencySymbol prop
                    type={isNigeria || currency === "NGN" ? "amount" : "string"}
                    currencySymbol={getCurrencySymbol("NGN")}
                    value={field.value || 0}
                    formKey="downPayment"
                    handleChange={(_, val) =>
                      field.onChange(Number(val.replace(/\D/g, "")))
                    }
                    error={errors.downPayment?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flexBet}>
            {/* ── Monthly Income ── */}
            <div className={styles.input}>
              <Controller
                name="monthlyIncome"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Monthly Net Income"
                    placeholder="Enter your Monthly Net Income"
                    // Use "amount" only for NGN, otherwise plain "string" so we
                    // can prepend the correct symbol via the currencySymbol prop
                    type={isNigeria || currency === "NGN" ? "amount" : "string"}
                    currencySymbol={
                      isDiaspora && currency !== "NGN"
                        ? getCurrencySymbol(currency)
                        : undefined
                    }
                    value={field.value || 0}
                    formKey="monthlyIncome"
                    handleChange={(_, val) =>
                      field.onChange(Number(val.replace(/\D/g, "")))
                    }
                    error={errors.monthlyIncome?.message}
                  />
                )}
              />
            </div>

            {/* ── Has Co-borrower ── */}
            <div className={styles.input}>
              <Controller
                name="hasCoBorrower"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Do you have a Co-borrower?"
                    placeholder="Select"
                    type="dropdown"
                    value={
                      field.value === true
                        ? "Yes"
                        : field.value === false
                        ? "No"
                        : ""
                    }
                    onPressDropDown={() =>
                      toggleDropdown("hasCoBorrower", true)
                    }
                    error={errors.hasCoBorrower?.message}
                  />
                )}
              />
            </div>
          </div>

          {hasCoBorrower === true && (
            <div className={styles.flexBet}>
              {/* ── Co-borrower Currency (Diaspora only) ── */}
              {isDiaspora && (
                <div className={styles.input}>
                  <Controller
                    name="coBorrowerCurrency"
                    control={control}
                    render={({ field }) => (
                      <InputWithHeader
                        header="Co-borrower Income Currency"
                        placeholder="Select Currency"
                        type="dropdown"
                        value={field.value || ""}
                        onPressDropDown={() =>
                          toggleDropdown("coBorrowerCurrency", true)
                        }
                        error={errors.coBorrowerCurrency?.message}
                      />
                    )}
                  />
                </div>
              )}

              {/* ── Co-borrower Income ── */}
              <div className={styles.input}>
                <Controller
                  name="coBorrowerMonthlyIncome"
                  control={control}
                  render={({ field }) => (
                    <InputWithHeader
                      header="Monthly Net Income of Co-borrower"
                      placeholder="Enter Monthly Net Income of Co-borrower"
                      type={
                        isNigeria || coBorrowerCurrency === "NGN"
                          ? "amount"
                          : "string"
                      }
                      currencySymbol={
                        isDiaspora &&
                        coBorrowerCurrency &&
                        coBorrowerCurrency !== "NGN"
                          ? getCurrencySymbol(coBorrowerCurrency)
                          : undefined
                      }
                      value={field.value || 0}
                      formKey="coBorrowerMonthlyIncome"
                      handleChange={(_, val) =>
                        field.onChange(Number(val.replace(/\D/g, "")))
                      }
                      error={errors.coBorrowerMonthlyIncome?.message}
                    />
                  )}
                />
              </div>

              {/* ── Combined Income (NGN only) ── */}
              {showCombinedIncome && (
                <div className={styles.input}>
                  <InputWithHeader
                    header="Combined Monthly Net Income"
                    placeholder=""
                    type="amount"
                    value={
                      (monthlyIncome ?? 0) + (coBorrowerMonthlyIncome ?? 0)
                    }
                    editable={false}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── PFA section (Nigeria only) ── */}
          {isNigeria && (
            <>
              <div className={styles.flexBet}>
                <div className={styles.input}>
                  <Controller
                    name="hasRSA"
                    control={control}
                    render={({ field }) => (
                      <InputWithHeader
                        header="Do you have a Pensions Account (PFA)?"
                        placeholder="Select"
                        type="dropdown"
                        value={
                          field.value === true
                            ? "Yes"
                            : field.value === false
                            ? "No"
                            : ""
                        }
                        onPressDropDown={() => toggleDropdown("hasRSA", true)}
                        error={errors.hasRSA?.message}
                      />
                    )}
                  />
                </div>

                {hasRSA === true && (
                  <div className={styles.input}>
                    <Controller
                      name="useRSA"
                      control={control}
                      render={({ field }) => (
                        <InputWithHeader
                          header="Use 25% of your Pension Balance as Down Payment?"
                          placeholder="Select"
                          type="dropdown"
                          value={
                            field.value === true
                              ? "Yes"
                              : field.value === false
                              ? "No"
                              : ""
                          }
                          onPressDropDown={() => toggleDropdown("useRSA", true)}
                          error={errors.useRSA?.message}
                        />
                      )}
                    />
                  </div>
                )}
              </div>

              {useRSA === true && (
                <div className={styles.flexBet}>
                  <div className={styles.input}>
                    <Controller
                      name="rsaNumber"
                      control={control}
                      render={({ field }) => (
                        <InputWithHeader
                          header="RSA Number"
                          placeholder="Enter RSA Number"
                          type="string"
                          value={field.value || ""}
                          formKey="rsaNumber"
                          handleChange={(_, val) => field.onChange(val)}
                          error={errors.rsaNumber?.message}
                        />
                      )}
                    />
                  </div>

                  {/* ── Pension Admin dropdown ── */}
                  <div className={styles.input}>
                    <Controller
                      name="pensionAdmin"
                      control={control}
                      render={({ field }) => (
                        <InputWithHeader
                          header="Pension Administrator"
                          placeholder="Select Pension Administrator"
                          type="dropdown"
                          value={field.value || ""}
                          onPressDropDown={() =>
                            toggleDropdown("pensionAdmin", true)
                          }
                          error={errors.pensionAdmin?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <div className={styles["butt-cont"]}>
            <button type="submit" className={styles["submit-btn"]}>
              Check Eligibility
            </button>
          </div>
        </div>
      </form>

      <ModalLayout
        modalOpen={isEligible !== undefined}
        onClickOut={() => {
          if (isEligible) reset();
          setIsEligible(undefined);
        }}
      >
        <EligibilityModal
          rows={eligibilityRows}
          message={eligibilityMessage}
          onContinue={onContinue}
          onClose={() => {
            if (isEligible) reset();
            setIsEligible(undefined);
          }}
          isSuccess={isEligible === true}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={submitSuccessful}
        onClickOut={() => clickOutSuccessModal()}
      >
        <SuccessModal
          onClose={() => clickOutSuccessModal()}
          message="Your application has been submitted. We'll be in touch shortly."
        />
      </ModalLayout>

      {/* ── Modals ── */}
      <ModalLayout
        modalOpen={dropDownStates.location}
        onClickOut={() => toggleDropdown("location", false)}
      >
        <PopUp
          options={locationOptions.map((l) => l.name)}
          click={(name) => {
            const val =
              locationOptions.find((l) => l.name === name)?.value ?? 0;
            setValue("location", val, { shouldValidate: true });
            // Auto-set currency based on location
            if (val === 0)
              setValue("currency", "NGN", { shouldValidate: true });
            else setValue("currency", "", { shouldValidate: true });
            toggleDropdown("location", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.interestRate}
        onClickOut={() => toggleDropdown("interestRate", false)}
      >
        <PopUp
          options={interestRateOptions.map((l) => l.name)}
          click={(name) => {
            const val =
              interestRateOptions.find((l) => l.name === name)?.value ?? 0;
            setValue("interestRate", val, { shouldValidate: true });
            toggleDropdown("interestRate", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.applicantType}
        onClickOut={() => toggleDropdown("applicantType", false)}
      >
        <PopUp
          options={employmentTypeOptions.map((l) => l.name)}
          click={(name) => {
            const val =
              employmentTypeOptions.find((l) => l.name === name)?.value ?? 0;
            setValue("applicantType", val, { shouldValidate: true });
            toggleDropdown("applicantType", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.repaymentFrequency}
        onClickOut={() => toggleDropdown("repaymentFrequency", false)}
      >
        <PopUp
          options={repaymentFreqOptions.map((l) => l.name)}
          click={(name) => {
            const val =
              repaymentFreqOptions.find((l) => l.name === name)?.value ?? 0;
            setValue("repaymentFrequency", val, { shouldValidate: true });
            toggleDropdown("repaymentFrequency", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.currency}
        onClickOut={() => toggleDropdown("currency", false)}
      >
        <PopUp
          options={currencyOptions}
          click={(name) => {
            setValue("currency", name, { shouldValidate: true });
            toggleDropdown("currency", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.coBorrowerCurrency}
        onClickOut={() => toggleDropdown("coBorrowerCurrency", false)}
      >
        <PopUp
          options={currencyOptions}
          click={(name) => {
            setValue("coBorrowerCurrency", name, { shouldValidate: true });
            toggleDropdown("coBorrowerCurrency", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.hasCoBorrower}
        onClickOut={() => toggleDropdown("hasCoBorrower", false)}
      >
        <PopUp
          options={["Yes", "No"]}
          click={(name) => {
            setValue("hasCoBorrower", name === "Yes", { shouldValidate: true });
            toggleDropdown("hasCoBorrower", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.hasRSA}
        onClickOut={() => toggleDropdown("hasRSA", false)}
      >
        <PopUp
          options={["Yes", "No"]}
          click={(name) => {
            setValue("hasRSA", name === "Yes", { shouldValidate: true });
            toggleDropdown("hasRSA", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.useRSA}
        onClickOut={() => toggleDropdown("useRSA", false)}
      >
        <PopUp
          options={["Yes", "No"]}
          click={(name) => {
            setValue("useRSA", name === "Yes", { shouldValidate: true });
            toggleDropdown("useRSA", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.pensionAdmin}
        onClickOut={() => toggleDropdown("pensionAdmin", false)}
      >
        <PopUp
          options={pfaOptions}
          click={(name) => {
            setValue("pensionAdmin", name, { shouldValidate: true });
            toggleDropdown("pensionAdmin", false);
          }}
          title="SELECT PFA"
          searchable={true}
        />
      </ModalLayout>

    </>
  );
};

export default MortgageApplication;
